import { Switch } from '@/common/ui/switch'
import { Label } from '@/common/ui/label'
import useMutationUpdateGeneralSetting from '@/modules/client-setting/api/hooks/useMutationUpdateGeneralSetting'
import useQueryGetGeneralSetting from '@/modules/client-setting/api/hooks/useQueryGetGeneralSetting'
import { useState } from 'react'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { useQueryClient } from '@tanstack/react-query'
import { GeneralSettingQueryKeys } from '@/modules/client-setting/keys'
import useHasPermission from '@/common/hooks/useHasPermission'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import { Info } from 'lucide-react'
import ChannelReorderModal from '../ChannelReorderModal'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/common/ui/alert-dialog'

export function EnableDepositChannelChoiceToggle() {
	const queryClient = useQueryClient()
	const [isLoading, setIsLoading] = useState(false)
	const [showInfoDialog, setShowInfoDialog] = useState(false)
	const { data: generalSetting } = useQueryGetGeneralSetting()
	const { mutateAsync: updateGeneralSetting } =
		useMutationUpdateGeneralSetting()
	const hasWritePermission = useHasPermission(PermissionKeys.WEB_SETTING)

	const isEnabled = generalSetting?.enableDepositChannelChoice ?? false

	const performUpdate = async (value: boolean) => {
		setIsLoading(true)
		try {
			await updateGeneralSetting({
				payload: { enableDepositChannelChoice: value },
			})
			toast.success(
				value ? 'เปิดใช้งานทางเลือกการฝากแล้ว' : 'ปิดใช้งานทางเลือกการฝากแล้ว',
			)
			queryClient.invalidateQueries({
				queryKey: [GeneralSettingQueryKeys.GetGeneralSetting],
			})
		} catch (error) {
			showErrorToast(error as Error)
		} finally {
			setIsLoading(false)
		}
	}

	const handleChange = async (value: boolean) => {
		if (!hasWritePermission || !generalSetting) return
		if (value) {
			setShowInfoDialog(true)
		} else {
			await performUpdate(value)
		}
	}

	const handleConfirmEnable = async () => {
		setShowInfoDialog(false)
		await performUpdate(true)
	}

	const handleCancelEnable = () => {
		setShowInfoDialog(false)
	}

	if (!generalSetting) return null

	return (
		<>
			<div className="rounded-lg border p-4">
				<div className="flex items-center justify-between gap-4">
					<div className="space-y-0.5">
						<Label
							htmlFor="enableDepositChannelChoice"
							className="cursor-pointer text-sm font-medium"
						>
							เปิดใช้งานทางเลือกการฝาก
						</Label>
						<p className="text-xs text-muted-foreground">
							ผู้เล่นจะเห็นตัวเลือกช่องทางการฝาก เช่น QR Payment,
							โอนเงินผ่านธนาคาร, ทรู มันนี่ วอลเล็ท ก่อนทำรายการฝากเงิน
						</p>
					</div>
					<Switch
						id="enableDepositChannelChoice"
						checked={isEnabled}
						onCheckedChange={handleChange}
						disabled={isLoading || !hasWritePermission}
					/>
				</div>

				{isEnabled && (
					<div className="mt-3 flex items-center justify-between border-t pt-3">
						<p className="text-xs text-muted-foreground">
							ถ้าช่องทางที่ผู้เล่นเลือกไม่พร้อมใช้งาน
							ระบบจะสลับไปช่องทางถัดไปตามลำดับที่ตั้งไว้โดยอัตโนมัติ
						</p>
						<ChannelReorderModal />
					</div>
				)}
			</div>

			<AlertDialog open={showInfoDialog} onOpenChange={setShowInfoDialog}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<div className="flex items-center gap-2">
							<Info className="h-6 w-6 text-blue-500" />
							<AlertDialogTitle className="text-xl">
								เปิดใช้งานทางเลือกการฝาก
							</AlertDialogTitle>
						</div>
						<AlertDialogDescription className="space-y-4 pt-4 text-base">
							<p className="font-semibold text-foreground">
								เมื่อคุณเปิดใช้งาน ทางเลือกการฝาก
								ผู้เล่นจะเห็นตัวเลือกดังนี้ก่อนทำรายการฝากเงิน:
							</p>
							<div className="rounded-lg border-2 border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950/20">
								<ul className="mt-2 list-inside list-disc space-y-2 text-foreground">
									<li>
										<strong>QR Payment</strong> — แสดงภาพ QR Code
									</li>
									<li>
										<strong>โอนเงินผ่านธนาคาร</strong> — ชื่อบัญชี / เลขบัญชี /
										ธนาคาร
									</li>
									<li>
										<strong>ทรู มันนี่ วอลเล็ท</strong> — ชำระผ่าน P2P ทรู
										มันนี่
									</li>
								</ul>
							</div>
							<p className="text-sm text-muted-foreground">
								ผู้เล่นสามารถเลือกช่องทางที่ต้องการใช้ในการฝากเงินก่อนการทำรายการฝากเงิน
							</p>
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={handleCancelEnable}>
							ยกเลิก
						</AlertDialogCancel>
						<AlertDialogAction onClick={handleConfirmEnable}>
							เข้าใจแล้ว เปิดใช้งาน
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
